import { create } from 'zustand';
import { AuthSession, LoginCredentials, UserAccount } from '../types';
import { login as loginRequest, logout as logoutRequest } from '../api/endpoints/auth';
import { refreshAccessToken } from '../api/client';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  user: UserAccount | null;
  /** In-memory only — never persisted to localStorage/sessionStorage (XSS surface). Re-derived via silent refresh on reload. */
  accessToken: string | null;
  status: AuthStatus;

  /** Called once at app boot to silently restore a session from the httpOnly refresh cookie. */
  initSession: () => Promise<void>;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  /** Used by the API client's 401-retry interceptor once it has a fresh token. */
  setSession: (session: AuthSession) => void;
  clearSession: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: null,
  status: 'idle',

  initSession: async () => {
    set({ status: 'loading' });
    try {
      // Shared single-flight with the API client's 401-retry path — see
      // refreshAccessToken's doc comment for why this must be deduplicated.
      // On success it has already called setSession() with the full user;
      // nothing left to do here.
      await refreshAccessToken();
    } catch {
      set({ user: null, accessToken: null, status: 'unauthenticated' });
    }
  },

  login: async (credentials) => {
    const session = await loginRequest(credentials);
    set({ user: session.user, accessToken: session.accessToken, status: 'authenticated' });
  },

  logout: async () => {
    try {
      await logoutRequest();
    } catch {
      // Best-effort — even if the network call fails, drop the local session.
    }
    set({ user: null, accessToken: null, status: 'unauthenticated' });
  },

  setSession: (session) => set({ user: session.user, accessToken: session.accessToken, status: 'authenticated' }),

  clearSession: () => set({ user: null, accessToken: null, status: 'unauthenticated' }),
}));
